import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { ClientModules } from "./own-client.js";
import { Forbidden } from "./shares.js";
import { ContractTypeSchema, MeasureCategorySchema } from "./column-schemas.js";
/**
 * The four contract-type buckets that gate billing, template selection and
 * client-share `client_types` filters. The set matches `apps/web`'s
 * `ContractType` union. A literal here keeps the package independent from
 * app-level constants, and a mismatch surfaces as a decode failure.
 */
export const ContractType = ContractTypeSchema;
export const ListContractTypes = HttpApiEndpoint.get("listContractTypes", "/admin/contract-types").addSuccess(S.Struct({ items: S.Array(ContractType), total: S.Int }));
// ---------------------------------------------------------------------------
// clients — the admin add-client wizard / client-management screens' one
// read/write surface for the `clients` tenant row, including its module
// flags (`ClientModules` — the same shape `client.getClientModules` reads
// for the caller's own client, here addressable by id for an admin caller).
// ---------------------------------------------------------------------------
export const ClientRow = S.Struct({
    id: S.UUID,
    name: S.NullOr(S.String),
    company_name: S.NullOr(S.String),
    contract_type: S.NullOr(ContractType),
    domain_name: S.NullOr(S.String),
    email: S.NullOr(S.String),
    phone: S.NullOr(S.String),
    website_url: S.NullOr(S.String),
    registration_number: S.NullOr(S.String),
    billing_address: S.optional(S.NullOr(S.String)),
    notifications_enabled: S.optional(S.Boolean),
    logo: S.optional(S.NullOr(S.Struct({
        id: S.UUID,
        filename_download: S.String,
    }))),
    report_design_id: S.optional(S.NullOr(S.UUID)),
    sifa_id: S.NullOr(S.UUID),
    ama_id: S.NullOr(S.UUID),
    sso_enabled: S.Boolean,
    sso_providers: S.NullOr(S.String),
    num_licenses_ohs_officers: S.NullOr(S.Int),
    num_licenses_location_managers: S.NullOr(S.Int),
    num_locations: S.NullOr(S.Int),
    date_created: S.NullOr(S.String),
    /**
     * When set, the customer relationship has ended. Today this is a persisted
     * marker and a list predicate, nothing more: the row and everything it owns
     * stays queryable, and the client lists default to `archived_at IS NULL`.
     * It gates no access yet — login, automated mail and Measure Center
     * generation all still ignore it (PMO-214, PMO-216, PMO-215), so do not
     * read a set `archived_at` as "this customer's people are locked out".
     * Null is the active state. Optional on the wire so existing consumers that
     * never asked for it keep decoding.
     */
    archived_at: S.optional(S.NullOr(S.String)),
    /**
     * The erasure deadline, stamped at archive time and cleared on unarchive.
     * Null on an active customer. Art. 5(1)(e) requires erasure once the purpose
     * lapses, and an archive with no expiry date is indefinite storage under a
     * friendlier name — so the deadline is carried on the record rather than
     * being recomputed later from an archive date and a period that may have
     * changed in the meantime.
     */
    retention_until: S.optional(S.NullOr(S.String)),
    /**
     * The retention period agreed with this one customer. Null means nobody has
     * agreed one and archiving falls back to `DEFAULT_RETENTION_PERIOD_DAYS`;
     * the distinction is what lets a data-protection review find the customers
     * nobody has looked at.
     */
    retention_period_days: S.optional(S.NullOr(S.Int)),
    modules: ClientModules,
    /**
     * clients.categories is a comma-separated string; [] when unset — parity
     * with `client.getMyClient`'s `MyClient.categories`.
     */
    categories: S.Array(MeasureCategorySchema),
    /**
     * The add-client wizard's onboarding-progress record (`{ onboarding:
     * ClientOnboardingStep[] }` on the frontend) — opaque here, matching the
     * `S.NullOr(S.Unknown)` convention every other `metadata` jsonb column in
     * this contract already uses; the wizard narrows the shape client-side.
     */
    metadata: S.NullOr(S.Unknown),
});
export const ClientWriteFields = {
    name: S.NonEmptyString,
    billing_address: S.NullOr(S.String),
    contract_type: ContractType,
    domain_name: S.optional(S.NullOr(S.String)),
    website_url: S.optional(S.NullOr(S.String)),
    notifications_enabled: S.optional(S.Boolean),
    logo: S.optional(S.NullOr(S.UUID)),
    report_design_id: S.optional(S.NullOr(S.UUID)),
    sifa_id: S.optional(S.NullOr(S.UUID)),
    ama_id: S.optional(S.NullOr(S.UUID)),
    metadata: S.optional(S.NullOr(S.Unknown)),
};
export const CreateClientPayload = S.Struct(ClientWriteFields);
export const CreateClient = HttpApiEndpoint.post("createClient", "/admin/clients/create")
    .setPayload(CreateClientPayload)
    .addSuccess(S.Struct({ data: ClientRow }))
    .addError(Forbidden, { status: 403 });
export const ListClientsParams = S.extend(PagingParams, S.Struct({
    search: S.optional(S.String),
    /**
     * Archived customers are omitted unless asked for. Churned customers
     * accumulate, and the list an admin works from every day should hold the
     * ones they still work with; the archived set is one filter away.
     */
    include_archived: S.optional(S.BooleanFromString),
    /**
     * Only the customers the caller is the contact SiFa or AMA for
     * (`clients.sifa_id` / `clients.ama_id`) — the customer overview's
     * "clients assigned to me" view.
     */
    assigned_to_me: S.optional(S.BooleanFromString),
}));
/**
 * Every tenant an OHS Admin can see; SiFa, AMA and External SiFa lists
 * narrow to `Principal.assignedClientIds` server-side — the caller never
 * supplies a client-id filter to reach that scoping, matching
 * `sharesForUser`'s own no-cross-scope-param rule.
 */
export const ListClients = HttpApiEndpoint.get("listClients", "/admin/clients")
    .setUrlParams(ListClientsParams)
    .addSuccess(pagedResponse(ClientRow));
export const ClientNotFound = S.Struct({
    _tag: S.Literal("ClientNotFound"),
});
export const GetClientParams = S.Struct({ id: S.UUID });
export const GetClient = HttpApiEndpoint.get("getClient", "/admin/clients/get")
    .setUrlParams(GetClientParams)
    .addSuccess(S.Struct({ data: ClientRow }))
    .addError(ClientNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * Every field is optional — this is a single patch intent covering the
 * wizard's separate pages (basic info, licenses, modules) rather than one
 * endpoint per page; the caller sends only the fields the page it's on
 * collects.
 */
/**
 * The fallback retention period, in days, for a customer nobody has agreed one
 * with.
 *
 * For the data this archives PIMA is the processor, not the controller, so the
 * period is not ours to choose: `retention_period_days` per customer is the
 * real mechanism and this is only the floor for a customer nobody set one for.
 *
 * 42 days is the Auftragsverarbeitungsvertrag's own figure. §7(2) gives the
 * controller its choice of deletion or return "innerhalb von 30 Arbeitstagen"
 * after the service agreement ends, which is six weeks of calendar time. It is
 * an approximation in one direction only — public holidays make 30 working days
 * slightly longer than 42 calendar days, so this errs toward deleting early,
 * which is the safe direction for a processor.
 *
 * The AVV's carve-out, "sofern keine gesetzlichen Aufbewahrungspflichten
 * entgegenstehen", is where the long periods live: medical documentation runs
 * 10 to 40 years and exposure records several decades. Those are statutory
 * exceptions to this floor, not reasons to raise it.
 */
export const DEFAULT_RETENTION_PERIOD_DAYS = 42;
/**
 * One day to a hundred years. The bounds are not a policy — they only keep a
 * typo out of a column an erasure job will act on. The ceiling is wide because
 * the real periods here span orders of magnitude: a processor's return window
 * is days, medical documentation runs 10 to 40 years, and exposure records
 * several decades.
 */
export const RetentionPeriodDays = S.Int.pipe(S.between(1, 36500));
export const UpdateClientPayload = S.Struct({
    id: S.UUID,
    name: S.optional(S.String),
    company_name: S.optional(S.NullOr(S.String)),
    contract_type: S.optional(ContractType),
    domain_name: S.optional(S.NullOr(S.String)),
    email: S.optional(S.NullOr(S.String)),
    phone: S.optional(S.NullOr(S.String)),
    website_url: S.optional(S.NullOr(S.String)),
    registration_number: S.optional(S.NullOr(S.String)),
    billing_address: S.optional(S.NullOr(S.String)),
    notifications_enabled: S.optional(S.Boolean),
    logo: S.optional(S.NullOr(S.UUID)),
    report_design_id: S.optional(S.NullOr(S.UUID)),
    sifa_id: S.optional(S.NullOr(S.UUID)),
    ama_id: S.optional(S.NullOr(S.UUID)),
    sso_enabled: S.optional(S.Boolean),
    sso_providers: S.optional(S.NullOr(S.String)),
    num_licenses_ohs_officers: S.optional(S.NullOr(S.NonNegativeInt)),
    num_licenses_location_managers: S.optional(S.NullOr(S.NonNegativeInt)),
    num_locations: S.optional(S.NullOr(S.Int)),
    modules: S.optional(S.partial(ClientModules)),
    /** Comma-joined category keys (`safety,health`) — the raw `clients.categories` encoding. */
    categories: S.optional(S.NullOr(S.String)),
    /** The wizard's onboarding-progress record — opaque, matching `ClientRow.metadata`. */
    metadata: S.optional(S.NullOr(S.Unknown)),
    /**
     * The retention period agreed with this customer; null puts it back to the
     * default. Only read at archive time, so changing it does not move a
     * deadline already stamped.
     */
    retention_period_days: S.optional(S.NullOr(RetentionPeriodDays)),
});
export const UpdateClient = HttpApiEndpoint.post("updateClient", "/admin/clients/update")
    .setPayload(UpdateClientPayload)
    .addSuccess(S.Struct({ data: ClientRow }))
    .addError(ClientNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteClientPayload = S.Struct({ id: S.UUID });
export const DeleteClient = HttpApiEndpoint.post("deleteClient", "/admin/clients/delete")
    .setPayload(DeleteClientPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(ClientNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * Archive / unarchive — the reversible counterpart to `deleteClient`, for the
 * ordinary case of a contract ending rather than a GDPR erasure. `deleteClient`
 * stays exactly as it is for the cases that genuinely need the data gone.
 *
 * Both intents are idempotent: archiving an archived customer leaves the
 * original `archived_at` alone rather than restamping it, so the date keeps
 * meaning "when the relationship ended". Both return the full row, so the
 * caller re-renders from the response instead of guessing.
 *
 * What archiving does NOT do, in this slice: it does not end anyone's session,
 * refuse their next login, stop a queued mail, or halt Measure Center
 * generation. Those gates are PMO-214, PMO-216 and PMO-215. So an OHS admin
 * calling this endpoint directly hides the customer from the default list while
 * its employees keep working — the archive is bookkeeping until those land.
 * No portal surface reaches these intents yet (PMO-217 adds the button), which
 * is what keeps the gap off the product; PMO-217 must not ship before PMO-214.
 * `archiving does not revoke access` in admin-clients.int.test.ts pins this
 * deliberately, so the day a gate lands that test fails and says so.
 */
export const ArchiveClientPayload = S.Struct({ id: S.UUID });
export const ArchiveClient = HttpApiEndpoint.post("archiveClient", "/admin/clients/archive")
    .setPayload(ArchiveClientPayload)
    .addSuccess(S.Struct({ data: ClientRow }))
    .addError(ClientNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const UnarchiveClientPayload = S.Struct({ id: S.UUID });
export const UnarchiveClient = HttpApiEndpoint.post("unarchiveClient", "/admin/clients/unarchive")
    .setPayload(UnarchiveClientPayload)
    .addSuccess(S.Struct({ data: ClientRow }))
    .addError(ClientNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// client_auth_domains — the SSO-domain allowlist per tenant.
// ---------------------------------------------------------------------------
export const ClientAuthDomainRow = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    domain: S.String,
    is_active: S.Boolean,
    date_created: S.NullOr(S.String),
    date_updated: S.NullOr(S.String),
});
export const ListClientAuthDomainsParams = S.Struct({ client_id: S.UUID });
export const ListClientAuthDomains = HttpApiEndpoint.get("listClientAuthDomains", "/admin/client-auth-domains")
    .setUrlParams(ListClientAuthDomainsParams)
    .addSuccess(S.Struct({ items: S.Array(ClientAuthDomainRow), total: S.Int }))
    .addError(Forbidden, { status: 403 });
export const CreateClientAuthDomainPayload = S.Struct({
    client_id: S.UUID,
    domain: S.NonEmptyString,
});
export const DomainTaken = S.Struct({ _tag: S.Literal("DomainTaken") });
export const CreateClientAuthDomain = HttpApiEndpoint.post("createClientAuthDomain", "/admin/client-auth-domains/create")
    .setPayload(CreateClientAuthDomainPayload)
    .addSuccess(S.Struct({ data: ClientAuthDomainRow }))
    .addError(DomainTaken, { status: 409 })
    .addError(Forbidden, { status: 403 });
export const ClientAuthDomainNotFound = S.Struct({
    _tag: S.Literal("ClientAuthDomainNotFound"),
});
/**
 * Both fields optional — the SSO-domain screen's one patch intent covers the
 * active/inactive toggle and the domain-text edit from the same row action,
 * matching `UpdateClientPayload`'s single-patch-intent convention above.
 */
export const UpdateClientAuthDomainPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    domain: S.optional(S.NonEmptyString),
    is_active: S.optional(S.Boolean),
});
export const UpdateClientAuthDomain = HttpApiEndpoint.post("updateClientAuthDomain", "/admin/client-auth-domains/update")
    .setPayload(UpdateClientAuthDomainPayload)
    .addSuccess(S.Struct({ data: ClientAuthDomainRow }))
    .addError(ClientAuthDomainNotFound, { status: 404 })
    .addError(DomainTaken, { status: 409 })
    .addError(Forbidden, { status: 403 });
export const DeleteClientAuthDomainPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
});
export const DeleteClientAuthDomain = HttpApiEndpoint.post("deleteClientAuthDomain", "/admin/client-auth-domains/delete")
    .setPayload(DeleteClientAuthDomainPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(ClientAuthDomainNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const adminClientsGroup = HttpApiGroup.make("admin-clients")
    .add(ListContractTypes)
    .add(ListClients)
    .add(GetClient)
    .add(CreateClient)
    .add(UpdateClient)
    .add(DeleteClient)
    .add(ArchiveClient)
    .add(UnarchiveClient)
    .add(ListClientAuthDomains)
    .add(CreateClientAuthDomain)
    .add(UpdateClientAuthDomain)
    .add(DeleteClientAuthDomain)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-clients.js.map